import { type ReactNode } from 'react';

import { CloseIcon } from '../icons';
import { ModalPortal } from '../modal-portal';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'responsive-wide' | 'responsive-normal';
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  // Responsive percentage widths
  'responsive-wide': 'w-[70vw] max-w-[650px]',
  'responsive-normal': 'w-full md:w-[70vw] lg:w-[60vw] max-w-4xl',
};

export const BaseModal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  className = '',
}: BaseModalProps) => {
  return (
    <ModalPortal isOpen={isOpen} onClose={onClose}>
      <div
        className={`bg-zinc-900 rounded-lg border border-zinc-600 w-full ${sizeClasses[size]} mx-auto max-h-full overflow-y-auto relative ${className}`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-400 hover:text-white transition-colors z-10 cursor-pointer"
          aria-label="Close"
        >
          <CloseIcon className="w-5 h-5" />
        </button>

        {/* Content Container */}
        <div className="p-4">
          {/* Header */}
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            {description && <p className="text-zinc-400 text-sm mt-1">{description}</p>}
          </div>

          {/* Body */}
          {children}
        </div>
      </div>
    </ModalPortal>
  );
};
